import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../services/api';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';
import GaugeMeter from '../../components/ui/GaugeMeter';
import {
  getMetricBandInfo,
  getARSInterpretation,
  getPCSInterpretation,
  getLIIInterpretation,
} from '../../utils/reportingConstants';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-neutral-100 text-neutral-700', icon: '📋' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: '🔄' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: '✅' },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: '🔴' },
};

// Normalize AI insights so the UI tolerates both Phase 2's richer schema
// (briefSummary / attentionItems[{item,context,urgency}] / supportActions[{action,reason}] / reinforcementTip / nextConversation)
// and phase1's simpler schema (summary / attentionItems[string] / supportActions[string] / reinforcementTips).
const normalizeAIInsights = (raw) => {
  if (!raw) return null;
  const briefSummary = raw.briefSummary || raw.summary || '';
  const attentionItems = (raw.attentionItems || []).map(a => {
    if (typeof a === 'string') return { item: a, context: '', urgency: 'medium' };
    return { item: a.item || '', context: a.context || '', urgency: a.urgency || 'medium' };
  });
  const coachingQuestions = raw.coachingQuestions || [];
  const supportActions = (raw.supportActions || []).map(a => {
    if (typeof a === 'string') return { action: a, reason: '' };
    return { action: a.action || '', reason: a.reason || '' };
  });
  const reinforcementTip = raw.reinforcementTip || raw.reinforcementTips || '';
  const nextConversation = raw.nextConversation || '';
  return { briefSummary, attentionItems, coachingQuestions, supportActions, reinforcementTip, nextConversation };
};

const ManagerPortalPage = () => {
  const { managerCode: urlCode } = useParams();
  const [code, setCode] = useState(urlCode || '');
  const [managerName, setManagerName] = useState(() => localStorage.getItem('managerName') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  // Free-form chat: manager asks AI a question about their team member.
  const [askInput, setAskInput] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askHistory, setAskHistory] = useState([]);
  const [activeNav, setActiveNav] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Auto-fetch AI insights once data is loaded.
  useEffect(() => {
    if (data?.participant?._id && !aiInsights && !aiLoading) {
      fetchAIInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleAccess = async () => {
    if (!code.trim()) { toast.error('Please enter a manager code'); return; }
    if (!managerName.trim()) { toast.error('Please enter your name'); return; }
    localStorage.setItem('managerName', managerName.trim());
    setLoading(true);
    try {
      const response = await api.post('/manager/access', {
        managerCode: code.trim(),
        managerName: managerName.trim(),
      });
      if (response.success) setData(response.data);
    } catch (error) {
      toast.error(error.message || 'Invalid manager code');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      const response = await api.post('/manager/access', {
        managerCode: code.trim(),
        managerName,
      });
      if (response.success) setData(response.data);
    } catch { /* ignore */ }
  };

  const handleAddComment = async (planIndex) => {
    const text = commentText[planIndex]?.trim();
    if (!text) return;
    setSaving(true);
    try {
      await api.post(`/manager/${code.trim()}/action-plans/${planIndex}/comment`, {
        text,
        managerName,
      });
      toast.success('Comment added!');
      setCommentText(prev => ({ ...prev, [planIndex]: '' }));
      refreshData();
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (planIndex) => {
    setSaving(true);
    try {
      await api.post(`/manager/${code.trim()}/action-plans/${planIndex}/validate`, {
        note: `Validated by ${managerName}`,
        managerName,
      });
      toast.success('Action plan validated!');
      refreshData();
    } catch {
      toast.error('Failed to validate');
    } finally {
      setSaving(false);
    }
  };

  const fetchAIInsights = async () => {
    setAiLoading(true);
    try {
      // Phase 1 keys AI insights by managerCode, not participantId.
      const res = await api.get(`/ai/manager/${code.trim()}/insights`);
      if (res.success && !res.data?.error) {
        setAiInsights(normalizeAIInsights(res.data));
      } else {
        setAiInsights({ error: true, userMessage: res.data?.userMessage });
      }
    } catch (err) {
      console.error('[Manager AI] Error:', err);
      // The API client carries a friendly userMessage on AI busy / high-demand
      // errors (429 / 503); surface it instead of the static fallback text.
      setAiInsights({ error: true, userMessage: err?.userMessage });
    } finally {
      setAiLoading(false);
    }
  };

  const askAI = async () => {
    const question = askInput.trim();
    if (!question) return;
    setAskLoading(true);
    // Optimistically append the question so the manager sees their own message immediately.
    const placeholder = { question, answer: null, suggestedActions: [], conversationStarters: [], watchOutFor: '', pending: true };
    setAskHistory(prev => [...prev, placeholder]);
    setAskInput('');
    try {
      const res = await api.post(`/ai/manager/${code.trim()}/ask`, { question });
      if (res.success && res.data) {
        const { answer = '', suggestedActions = [], conversationStarters = [], watchOutFor = '' } = res.data;
        setAskHistory(prev => prev.map((h, i) => (
          i === prev.length - 1 ? { question, answer, suggestedActions, conversationStarters, watchOutFor, pending: false } : h
        )));
      } else {
        toast.error(res.error || 'AI could not answer that');
        setAskHistory(prev => prev.slice(0, -1));
      }
    } catch (err) {
      toast.error(err.message || 'AI request failed');
      setAskHistory(prev => prev.slice(0, -1));
    } finally {
      setAskLoading(false);
    }
  };

  const svgIcon = (d) => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );

  // ============ LOGIN SCREEN ============
  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-white flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-neutral-200 p-6 sm:p-8 w-full max-w-md"
        >
          <div className="text-center mb-6">
            <img src={logoIcon} alt="Aktivity" className="w-12 h-12 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-neutral-900">Manager Portal</h1>
            <p className="text-sm text-neutral-500 mt-1">Enter the code shared by your team member</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Manager Code</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="MGR-XXXXXX"
                className="w-full px-4 py-3 border border-neutral-300 rounded-xl text-center text-lg font-mono font-bold tracking-wider focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                onKeyDown={(e) => e.key === 'Enter' && handleAccess()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Your Name</label>
              <input type="text" value={managerName} onChange={(e) => setManagerName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                onKeyDown={(e) => e.key === 'Enter' && handleAccess()}
              />
            </div>
            <button onClick={handleAccess} disabled={loading}
              className="w-full py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50"
            >{loading ? 'Loading...' : 'Access Dashboard'}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ============ DASHBOARD DATA ============
  // Phase1 backend returns: { participant: { _id, name, email, postSessionData }, session: { _id, name, ... } }
  // Action plans live under participant.postSessionData.actionPlans in phase1.
  const participant = data.participant || {};
  const session = data.session || {};
  const actionPlans = participant.postSessionData?.actionPlans || data.actionPlans || [];

  const completedCount = actionPlans.filter(a => a.status === 'completed').length;
  const inProgressCount = actionPlans.filter(a => a.status === 'in_progress').length;
  const blockedCount = actionPlans.filter(a => a.status === 'blocked').length;
  const completionPercent = actionPlans.length > 0 ? Math.round((completedCount / actionPlans.length) * 100) : 0;

  const navItems = [
    { id: 'overview', name: 'Overview', icon: "M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" },
    { id: 'action-plans', name: 'Action Plans', icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
    // Short label keeps the sidebar tidy — the full "Aktivity Intelligence™
    // (AI) Coaching Insights" branding lives in the page header below.
    { id: 'ai-insights', name: 'AI Insights', icon: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" },
  ];

  // ====== BRIEF AI SUMMARY CARD ======
  const renderAIBriefCard = () => {
    if (aiLoading) {
      return (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl border border-primary-100 p-5 mb-6">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
            <p className="text-sm text-primary-600 font-medium">AI is analyzing {participant.name}'s progress...</p>
          </div>
        </div>
      );
    }
    if (!aiInsights || aiInsights.error) return null;
    return (
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl border border-primary-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            <h3 className="text-sm font-bold text-primary-900">AI Quick Summary</h3>
          </div>
          <button onClick={() => setActiveNav('ai-insights')} className="text-xs text-primary-600 font-medium hover:text-primary-700">
            View Full Insights →
          </button>
        </div>
        {aiInsights.briefSummary && (
          <p className="text-sm text-neutral-700 leading-relaxed mb-3">{aiInsights.briefSummary}</p>
        )}
        {aiInsights.attentionItems?.length > 0 && (
          <div className="space-y-2">
            {aiInsights.attentionItems.slice(0, 2).map((item, idx) => (
              <div key={idx} className={`flex items-start gap-2 p-2.5 rounded-lg ${
                item.urgency === 'high' ? 'bg-red-50 border border-red-100' :
                item.urgency === 'medium' ? 'bg-amber-50 border border-amber-100' :
                'bg-blue-50 border border-blue-100'
              }`}>
                <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  item.urgency === 'high' ? 'bg-red-500' :
                  item.urgency === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                }`}></span>
                <div>
                  <p className="text-sm font-medium text-neutral-900">{item.item}</p>
                  {item.context && <p className="text-xs text-neutral-500">{item.context}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ====== OVERVIEW PAGE ======
  const renderOverview = () => (
    <div>
      {/* Welcome Header */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl font-bold text-primary-700">{(participant.name || 'P')[0].toUpperCase()}</span>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">{participant.name}</h1>
              <p className="text-neutral-500 text-sm">Participant in {session.name}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-primary-100 text-primary-700">
            Manager View
          </span>
        </div>
        <div className="pt-4 border-t border-neutral-200">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">Session</p>
              <h2 className="text-lg font-bold text-neutral-900 break-words">{session.name}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Participant Session-Level Report Card — mirrors the participant's own session overview */}
      {(participant.engagement || participant.performanceMetrics) && (
        <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-6">
          <h3 className="text-base font-bold text-neutral-900 mb-4">Session-Level Report</h3>
          {participant.engagement && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="p-3 bg-neutral-50 rounded-lg text-center">
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Sessions</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {participant.engagement.activitiesLaunched > 0 ? '1/1' : '0/1'}
                </p>
                <p className="text-[10px] text-neutral-400">{participant.engagement.activitiesLaunched > 0 ? '100%' : '0%'} attendance</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-xs text-green-600 uppercase tracking-wider mb-1">Engagement</p>
                <p className="text-2xl font-bold text-green-700">{participant.engagement.engagementScore}%</p>
                <p className="text-[10px] text-green-600">Overall score</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Activities</p>
                <p className="text-2xl font-bold text-blue-700">{participant.engagement.activitiesCompleted}/{participant.engagement.activitiesLaunched}</p>
                <p className="text-[10px] text-blue-600">completed</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-xs text-amber-600 uppercase tracking-wider mb-1">Rating Given</p>
                <p className="text-2xl font-bold text-amber-700">
                  {participant.engagement.sessionRating ? `${participant.engagement.sessionRating}/5` : '—'}
                </p>
                <p className="text-[10px] text-amber-600">Session rating</p>
              </div>
            </div>
          )}

          {participant.performanceMetrics && (() => {
            // Mirror the participant report's Performance Metrics card — same GaugeMeter
            // component, same 0-1 ARS scale, same bands/interpretation strings — so the
            // manager sees exactly what the participant sees.
            const pm = participant.performanceMetrics;
            const arsValue = pm.actionReadinessScore || 0;
            const pcsValue = pm.participantConfidenceSignal || 0;
            const liiValue = pm.learningImplementationIndicator || 0;
            const arsBand = getMetricBandInfo('ars', arsValue);
            const pcsBand = getMetricBandInfo('pcs', pcsValue);
            const liiBand = getMetricBandInfo('lii', liiValue);
            const hasData = arsValue > 0 || pcsValue > 0 || liiValue > 0;
            if (!hasData) return null;
            return (
              <div className="bg-white rounded-2xl border border-neutral-100 p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">Performance Metrics</h3>
                    <p className="text-xs text-neutral-500">Participant learning indicators</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {arsValue > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={arsValue}
                        maxValue={1}
                        label="Action Readiness"
                        band={arsBand?.label || 'Pending'}
                        color={arsBand?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-3 text-xs text-neutral-600 text-center">
                        {getARSInterpretation(arsValue, 'participant')}
                      </p>
                    </div>
                  )}
                  {pcsValue > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={pcsValue}
                        maxValue={5}
                        label="Confidence Signal"
                        band={pcsBand?.label || 'Pending'}
                        color={pcsBand?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-3 text-xs text-neutral-600 text-center">
                        {getPCSInterpretation(pcsValue, 'participant')}
                      </p>
                    </div>
                  )}
                  {liiValue > 0 && (
                    <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                      <GaugeMeter
                        value={liiValue}
                        maxValue={5}
                        label="Implementation Intent"
                        band={liiBand?.label || 'Pending'}
                        color={liiBand?.color || 'gray'}
                        size="sm"
                      />
                      <p className="mt-3 text-xs text-neutral-600 text-center">
                        {getLIIInterpretation(liiValue, 'participant')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Aggregated Action Plan Infographic — summarizes all plans for a quick manager glance */}
      {actionPlans.length > 0 && (() => {
        const totalPlans = actionPlans.length;
        const avgCommitment = actionPlans.reduce((sum, p) => sum + (p.commitment || 0), 0) / totalPlans;
        const totalNotes = actionPlans.reduce((sum, p) => sum + (p.progressNotes?.length || 0), 0);
        // Modal byWhen — most common target timeline across the participant's plans.
        const byWhenCounts = {};
        actionPlans.forEach(p => {
          if (p.byWhen) byWhenCounts[p.byWhen] = (byWhenCounts[p.byWhen] || 0) + 1;
        });
        const topByWhen = Object.entries(byWhenCounts).sort((a, b) => b[1] - a[1])[0];
        const modalTimeline = topByWhen ? topByWhen[0].replace(/_/g, ' ') : 'unspecified';
        // Dominant current status.
        const statusCounts = { completed: completedCount, in_progress: inProgressCount, blocked: blockedCount, not_started: totalPlans - completedCount - inProgressCount - blockedCount };
        const dominantStatus = Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0];
        const dominantLabel = (dominantStatus?.[0] || 'not_started').replace(/_/g, ' ');
        return (
          <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-6">
            <h3 className="text-base font-bold text-neutral-900 mb-1">Action Plans at a Glance</h3>
            <p className="text-xs text-neutral-500 mb-4">Aggregated across {participant.name}'s {totalPlans} plan{totalPlans === 1 ? '' : 's'}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  {svgIcon("M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z")}
                </div>
                <div>
                  <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider">Target Timeline</p>
                  <p className="text-sm font-bold text-neutral-900 capitalize">{modalTimeline}</p>
                  <p className="text-xs text-neutral-500">Most common across plans</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  {svgIcon("M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z")}
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider">Commitment Level</p>
                  <p className="text-sm font-bold text-neutral-900">{avgCommitment.toFixed(1)} out of 10</p>
                  <p className="text-xs text-neutral-500">Average across plans</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  {svgIcon("M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625z")}
                </div>
                <div>
                  <p className="text-xs text-neutral-500 font-semibold uppercase tracking-wider">Current Status</p>
                  <p className="text-sm font-bold text-neutral-900 capitalize">{dominantLabel}</p>
                  <p className="text-xs text-neutral-500">{completedCount} completed · {inProgressCount} in progress · {blockedCount} blocked</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  {svgIcon("M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z")}
                </div>
                <div>
                  <p className="text-xs text-neutral-500 font-semibold uppercase tracking-wider">Progress Notes</p>
                  <p className="text-sm font-bold text-neutral-900">{totalNotes} total</p>
                  <p className="text-xs text-neutral-500">Across all plans</p>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {renderAIBriefCard()}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
        <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {svgIcon("M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z")}
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Total Action Plans</p>
              <p className="text-xs sm:text-sm font-semibold text-neutral-900">{actionPlans.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {svgIcon("M4.5 12.75l6 6 9-13.5")}
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Completed</p>
              <p className="text-xs sm:text-sm font-semibold text-green-600">{completedCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {svgIcon("M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182")}
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">In Progress</p>
              <p className="text-xs sm:text-sm font-semibold text-blue-600">{inProgressCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {svgIcon("M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z")}
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Blocked</p>
              <p className="text-xs sm:text-sm font-semibold text-red-600">{blockedCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {actionPlans.length > 0 && (
        <div className="flex items-center gap-3 p-4 bg-primary-50 border border-primary-100 rounded-xl mb-6">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
            {svgIcon("M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z")}
          </div>
          <div className="flex-1">
            <p className="text-xs text-primary-600 font-medium uppercase tracking-wider">Overall Progress</p>
            <div className="flex items-center gap-3 mt-1">
              <div className="flex-1 bg-primary-100 rounded-full h-2.5">
                <div className="bg-primary-500 h-2.5 rounded-full transition-all" style={{ width: `${completionPercent}%` }}></div>
              </div>
              <span className="text-sm font-bold text-primary-700">{completionPercent}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Action Plan Summary + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h3 className="text-base font-bold text-neutral-900 mb-4 flex items-center gap-2">
            {svgIcon("M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z")}
            Action Plans
          </h3>
          <div className="space-y-2">
            {actionPlans.map((plan, idx) => {
              const cfg = STATUS_CONFIG[plan.status] || STATUS_CONFIG.not_started;
              return (
                <button key={idx} onClick={() => setActiveNav('action-plans')}
                  className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-neutral-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{cfg.icon}</span>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">{plan.title || `Plan ${idx + 1}`}</p>
                      {plan.progressNotes?.length > 0 && <p className="text-xs text-neutral-400">{plan.progressNotes.length} notes</p>}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                </button>
              );
            })}
            {actionPlans.length === 0 && (
              <p className="text-sm text-neutral-400 text-center py-4">No action plans yet</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h3 className="text-base font-bold text-neutral-900 mb-4 flex items-center gap-2">
            {svgIcon("M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z")}
            Quick Actions
          </h3>
          <div className="space-y-3">
            <button onClick={() => setActiveNav('ai-insights')}
              className="w-full flex items-start gap-3 p-3 rounded-xl bg-primary-50 hover:bg-primary-100 transition-colors text-left"
            >
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
              <div>
                <p className="text-sm font-medium text-neutral-900">Get AI Coaching Suggestions</p>
                <p className="text-xs text-neutral-500">AI-powered tips on how to support {participant.name}</p>
              </div>
            </button>
            <button onClick={() => setActiveNav('action-plans')}
              className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors text-left"
            >
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
              <div>
                <p className="text-sm font-medium text-neutral-900">Review Action Plans</p>
                <p className="text-xs text-neutral-500">Add comments and validate progress</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ====== ACTION PLANS PAGE ======
  const renderActionPlans = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Action Plans</h1>
        <p className="text-base text-neutral-500 mt-1">Review {participant.name}'s action plans, add comments, and validate progress</p>
      </div>
      {actionPlans.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 border text-center">
          <p className="text-lg font-medium text-neutral-500">No action plans yet</p>
        </div>
      ) : (
        <div className="space-y-5">
          {actionPlans.map((plan, idx) => {
            const cfg = STATUS_CONFIG[plan.status] || STATUS_CONFIG.not_started;
            return (
              <div key={idx} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-sm">
                <div className={`h-1.5 ${plan.status === 'completed' ? 'bg-gradient-to-r from-green-400 to-green-500' : plan.status === 'in_progress' ? 'bg-gradient-to-r from-blue-400 to-blue-500' : plan.status === 'blocked' ? 'bg-gradient-to-r from-red-400 to-red-500' : 'bg-neutral-200'}`}></div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-neutral-900">{plan.title || `Plan ${idx + 1}`}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                        {plan.byWhen && (
                          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                              {svgIcon("M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z")}
                            </div>
                            <div>
                              <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Target Timeline</p>
                              <p className="text-sm font-semibold text-neutral-900">{plan.byWhen.replace(/_/g, ' ')}</p>
                              <p className="text-xs text-neutral-500 mt-0.5">Time frame to complete this action</p>
                            </div>
                          </div>
                        )}
                        {plan.commitment != null && (
                          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                              {svgIcon("M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z")}
                            </div>
                            <div>
                              <p className="text-xs text-amber-600 font-medium uppercase tracking-wider">Commitment Level</p>
                              <p className="text-sm font-semibold text-neutral-900">{plan.commitment} out of 10</p>
                              <p className="text-xs text-neutral-500 mt-0.5">How committed the participant is to this action</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                          <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                            {svgIcon("M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z")}
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500 font-medium uppercase tracking-wider">Current Status</p>
                            <p className="text-sm font-semibold text-neutral-900">{cfg.icon} {cfg.label}</p>
                            <p className="text-xs text-neutral-500 mt-0.5">Last updated by participant</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                          <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                            {svgIcon("M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z")}
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500 font-medium uppercase tracking-wider">Progress Notes</p>
                            <p className="text-sm font-semibold text-neutral-900">{plan.progressNotes?.length || 0} notes</p>
                            <p className="text-xs text-neutral-500 mt-0.5">Updates from participant and manager</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold flex-shrink-0 ${cfg.color}`}>{cfg.icon} {cfg.label}</span>
                  </div>

                  {plan.managerValidated && (
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl mb-4 border border-green-100">
                      {svgIcon("M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z")}
                      <div>
                        <p className="text-sm font-semibold text-green-800">You validated this plan</p>
                        <p className="text-xs text-green-600">The participant has been notified</p>
                      </div>
                    </div>
                  )}

                  {plan.progressNotes?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-neutral-700 mb-3">Progress Notes ({plan.progressNotes.length})</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {plan.progressNotes.map((note, nIdx) => (
                          <div key={nIdx} className={`p-4 rounded-xl ${note.author === 'manager' ? 'bg-primary-50 border border-primary-100' : 'bg-neutral-50 border border-neutral-100'}`}>
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-semibold text-sm flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${note.author === 'manager' ? 'bg-primary-200 text-primary-700' : 'bg-neutral-200 text-neutral-700'}`}>
                                  {(note.authorName || (note.author === 'manager' ? 'M' : 'P'))[0]}
                                </span>
                                {note.authorName || (note.author === 'manager' ? managerName || 'Manager' : participant.name)}
                              </span>
                              <span className="text-xs text-neutral-400">{note.createdAt ? new Date(note.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                            </div>
                            <p className="text-sm text-neutral-700 leading-relaxed">{note.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-neutral-100 space-y-3">
                    <p className="text-sm font-semibold text-neutral-700">Add Comment</p>
                    <div className="flex gap-2">
                      <input type="text" value={commentText[idx] || ''}
                        onChange={(e) => setCommentText(prev => ({ ...prev, [idx]: e.target.value }))}
                        placeholder="Add a comment or suggestion..."
                        className="flex-1 px-4 py-3 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddComment(idx)}
                      />
                      <button onClick={() => handleAddComment(idx)} disabled={saving || !commentText[idx]?.trim()}
                        className="px-5 py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 disabled:opacity-30 transition-colors"
                      >Comment</button>
                    </div>
                    {!plan.managerValidated && (plan.status === 'completed' || plan.status === 'in_progress') && (
                      <button onClick={() => handleValidate(idx)} disabled={saving}
                        className="w-full py-3 border-2 border-green-500 text-green-600 rounded-xl text-sm font-bold hover:bg-green-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                      >
                        {svgIcon("M9 12l2 2 4-4")}
                        Validate Progress
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ====== AI INSIGHTS FULL PAGE ======
  const renderAIInsights = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Aktivity Intelligence™ (AI) Insights</h1>
        <p className="text-base text-neutral-500 mt-1">AI-powered suggestions based on {participant.name}'s actual challenges, action plans, and reflections</p>
      </div>

      {aiLoading && (
        <div className="bg-white rounded-2xl border p-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-sm text-primary-600 font-medium">AI is analyzing {participant.name}'s progress...</p>
          <p className="text-xs text-neutral-400 mt-1">This may take a few seconds</p>
        </div>
      )}

      {aiInsights?.error && (
        <div className="bg-white rounded-2xl border p-8 text-center">
          <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <p className="text-lg font-bold text-neutral-900 mb-2">AI Not Available</p>
          <p className="text-sm text-neutral-500 mb-4">{aiInsights.userMessage || 'AI coaching is temporarily unavailable. Please try again in a few minutes.'}</p>
          <button onClick={() => { setAiInsights(null); fetchAIInsights(); }}
            className="px-6 py-2 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-200"
          >Try Again</button>
        </div>
      )}

      {aiInsights && !aiInsights.error && (
        <div className="space-y-5">
          {aiInsights.briefSummary && (
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl border border-primary-100 p-6">
              <h3 className="text-lg font-bold text-primary-900 mb-2">Summary</h3>
              <p className="text-base text-neutral-700 leading-relaxed">{aiInsights.briefSummary}</p>
            </div>
          )}

          {aiInsights.attentionItems?.length > 0 && (
            <div className="bg-white rounded-2xl border p-6">
              <h3 className="text-lg font-bold text-neutral-900 mb-1">Items Needing Attention</h3>
              <p className="text-sm text-neutral-500 mb-4">Priority areas to address with {participant.name}</p>
              <div className="space-y-3">
                {aiInsights.attentionItems.map((item, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-4 rounded-xl border ${
                    item.urgency === 'high' ? 'bg-red-50 border-red-200' :
                    item.urgency === 'medium' ? 'bg-amber-50 border-amber-200' :
                    'bg-blue-50 border-blue-200'
                  }`}>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase flex-shrink-0 ${
                      item.urgency === 'high' ? 'bg-red-200 text-red-800' :
                      item.urgency === 'medium' ? 'bg-amber-200 text-amber-800' :
                      'bg-blue-200 text-blue-800'
                    }`}>{item.urgency}</span>
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">{item.item}</p>
                      {item.context && <p className="text-sm text-neutral-500 mt-0.5">{item.context}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {aiInsights.coachingQuestions?.length > 0 && (
            <div className="bg-white rounded-2xl border p-6">
              <h3 className="text-lg font-bold text-neutral-900 mb-1">Coaching Questions</h3>
              <p className="text-sm text-neutral-500 mb-4">Use these in your next 1-on-1 with {participant.name}</p>
              <div className="space-y-3">
                {aiInsights.coachingQuestions.map((q, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-primary-50 rounded-xl border border-primary-100">
                    <span className="w-7 h-7 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{idx + 1}</span>
                    <p className="text-sm text-neutral-800 leading-relaxed">"{q}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {aiInsights.supportActions?.length > 0 && (
            <div className="bg-white rounded-2xl border p-6">
              <h3 className="text-lg font-bold text-neutral-900 mb-1">Support Actions</h3>
              <p className="text-sm text-neutral-500 mb-4">Things you can do to help {participant.name} succeed</p>
              <div className="space-y-3">
                {aiInsights.supportActions.map((a, idx) => (
                  <div key={idx} className="p-4 bg-white rounded-xl border border-neutral-200">
                    <p className="text-base font-semibold text-neutral-900">{a.action}</p>
                    {a.reason && <p className="text-sm text-neutral-500 mt-1">{a.reason}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {aiInsights.reinforcementTip && (
            <div className="bg-green-50 rounded-2xl border border-green-200 p-6">
              <h3 className="text-lg font-bold text-green-900 mb-2">Positive Reinforcement</h3>
              <p className="text-base text-green-800 leading-relaxed">{aiInsights.reinforcementTip}</p>
            </div>
          )}

          {aiInsights.nextConversation && (
            <div className="bg-indigo-50 rounded-2xl border border-indigo-200 p-6">
              <h3 className="text-lg font-bold text-indigo-900 mb-2">For Your Next 1-on-1</h3>
              <p className="text-base text-indigo-800 leading-relaxed">{aiInsights.nextConversation}</p>
            </div>
          )}

          <div className="text-center">
            <button onClick={() => { setAiInsights(null); fetchAIInsights(); }}
              className="px-6 py-2 text-sm text-primary-600 font-medium hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
            >Refresh Insights</button>
          </div>
        </div>
      )}

      {/* Free-form Q&A — manager asks AI a specific question about the participant. */}
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl border border-indigo-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl" role="img" aria-label="AI">🤖</span>
          <div>
            <h3 className="text-lg font-bold text-neutral-900">Ask AI</h3>
            <p className="text-xs text-neutral-500">Get specific guidance grounded in their actual action plans, challenges, and progress</p>
          </div>
        </div>

        {/* Conversation history */}
        {askHistory.length > 0 && (
          <div className="space-y-4 max-h-[28rem] overflow-y-auto">
            {askHistory.map((turn, idx) => (
              <div key={idx} className="space-y-2">
                {/* Manager's question bubble */}
                <div className="flex justify-end">
                  <div className="max-w-[85%] bg-primary-500 text-white px-4 py-2.5 rounded-2xl rounded-tr-sm shadow-sm">
                    <p className="text-sm leading-relaxed">{turn.question}</p>
                  </div>
                </div>

                {/* AI response */}
                <div className="flex justify-start">
                  <div className="max-w-[90%] bg-white border border-indigo-200 rounded-2xl rounded-tl-sm shadow-sm p-4 space-y-3">
                    {turn.pending ? (
                      <div className="flex items-center gap-2 text-sm text-neutral-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                        <span>Thinking...</span>
                      </div>
                    ) : (
                      <>
                        {turn.answer && (
                          <p className="text-sm text-neutral-800 leading-relaxed">{turn.answer}</p>
                        )}
                        {turn.suggestedActions?.length > 0 && (
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-1.5">Suggested Actions</p>
                            <ul className="space-y-1.5">
                              {turn.suggestedActions.map((a, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                                  <span>{a}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {turn.conversationStarters?.length > 0 && (
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-1.5">Try Saying</p>
                            <div className="space-y-1.5">
                              {turn.conversationStarters.map((c, i) => (
                                <p key={i} className="text-sm italic text-neutral-700 bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-100">"{c}"</p>
                              ))}
                            </div>
                          </div>
                        )}
                        {turn.watchOutFor && (
                          <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-100">
                            <span className="font-semibold">Watch out:</span> {turn.watchOutFor}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-neutral-800">Your question</label>
          <textarea
            value={askInput}
            onChange={(e) => setAskInput(e.target.value)}
            rows={2}
            placeholder="e.g. How should I talk to them about their blocked action plan?"
            className="w-full px-4 py-3 border border-indigo-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!askLoading && askInput.trim()) askAI();
              }
            }}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-400">Enter to send · Shift+Enter for a new line</p>
            <button
              onClick={askAI}
              disabled={askLoading || !askInput.trim()}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {askLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Asking...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.125A59.769 59.769 0 0121.485 12 59.768 59.768 0 013.27 20.875L5.999 12zm0 0h7.5" />
                  </svg>
                  <span>Ask AI</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeNav) {
      case 'overview': return renderOverview();
      case 'action-plans': return renderActionPlans();
      case 'ai-insights': return renderAIInsights();
      default: return renderOverview();
    }
  };

  const getPageTitle = () => navItems.find(n => n.id === activeNav)?.name || 'Manager Dashboard';

  // ============ MAIN LAYOUT ============
  return (
    <div className="min-h-screen bg-white flex">
      {/* Sidebar */}
      <motion.aside initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img src={logoFull} alt="Aktivity" className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input type="text" placeholder="Search" className="w-full pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-lg text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.id}>
                <button onClick={() => setActiveNav(item.id)}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    activeNav === item.id
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  {activeNav === item.id && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${activeNav === item.id ? 'text-primary-600' : ''}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                    </svg>
                  </span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >{item.name}</motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-neutral-200">
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <svg className="w-5 h-5 transition-transform" style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}
            ><path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">{getPageTitle()}</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-xs font-medium hidden sm:inline">{managerName}</span>
              </div>
              <button onClick={() => { setData(null); setAiInsights(null); setActiveNav('overview'); }}
                className="px-3 py-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
              >Logout</button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 bg-white overflow-y-auto pb-40 lg:pb-6">
          <div className="lg:hidden mb-4">
            <h1 className="text-lg font-bold text-neutral-900">{getPageTitle()}</h1>
          </div>
          {renderContent()}
          {/* Explicit mobile-only spacer. Belt-and-suspenders so the last input /
              submit button is never overlapped by the fixed mobile bottom nav,
              regardless of how the flex parent resolves overflow-y-auto. */}
          <div className="lg:hidden h-20" aria-hidden="true" />
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => setActiveNav(item.id)}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${activeNav === item.id ? '' : 'text-neutral-500'}`}
              style={activeNav === item.id ? { color: '#ff8a00' } : {}}
            >
              <span className="[&>svg]:w-4 [&>svg]:h-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'ai-insights' ? 'AI' : item.id === 'action-plans' ? 'Plans' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default ManagerPortalPage;
